import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ingredientsApi } from '@/api/ingredients';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';
import { getErrorMessage } from '@/api/client';
import {
  PageHeader, Button, Input, Badge, Spinner, EmptyState, ConfirmDialog,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Card,
} from '@/components/ui';
import { Plus, Pencil, Trash2, Search } from 'lucide-react';

export default function IngredientsListPage() {
  const { t }           = useTranslation();
  const { isAdmin }     = useAuth();
  const { toast }       = useToast();
  const qc              = useQueryClient();

  const [search,    setSearch]    = useState('');
  const [page,      setPage]      = useState(1);
  const [deleteId,  setDeleteId]  = useState<string | null>(null);
  const limit = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['ingredients', { page, search }],
    queryFn:  () => ingredientsApi.list({ page, limit, search: search || undefined }),
  });

  const deleteMutation = useMutation({
    mutationFn: ingredientsApi.delete,
    onSuccess: () => {
      toast(t('ingredients.deleted'));
      qc.invalidateQueries({ queryKey: ['ingredients'] });
      setDeleteId(null);
    },
    onError: (err) => toast(getErrorMessage(err), 'error'),
  });

  function formatCost(v: number | null | undefined) {
    return v != null ? `$${Number(v).toFixed(4)}` : '—';
  }

  return (
    <div>
      <PageHeader title={t('ingredients.title')}>
        {isAdmin && (
          <Button asChild size="sm">
            <Link to="/ingredients/new"><Plus size={16} />{t('ingredients.new')}</Link>
          </Button>
        )}
      </PageHeader>

      {/* Search */}
      <div className="mb-4 flex gap-2">
        <div className="relative flex-1 max-w-xs">
          <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder={t('common.search')}
            className="pl-9"
          />
        </div>
      </div>

      {/* Desktop table */}
      <Card className="hidden md:block">
        {isLoading ? (
          <Spinner className="py-16" />
        ) : data?.data.length === 0 ? (
          <EmptyState message={t('common.noResults')}>
            {isAdmin && (
              <Button asChild size="sm" variant="outline">
                <Link to="/ingredients/new"><Plus size={14} /> {t('ingredients.new')}</Link>
              </Button>
            )}
          </EmptyState>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('ingredients.name')}</TableHead>
                <TableHead>{t('ingredients.category')}</TableHead>
                <TableHead>{t('ingredients.supplier')}</TableHead>
                <TableHead className="text-right">{t('ingredients.grossCostPerKg')}</TableHead>
                <TableHead className="text-right">{t('ingredients.sugars')}</TableHead>
                <TableHead className="text-right">{t('ingredients.fat')}</TableHead>
                <TableHead className="text-right">{t('ingredients.pac')}</TableHead>
                <TableHead>Status</TableHead>
                {isAdmin && <TableHead />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.data.map(ing => (
                <TableRow key={ing.id}>
                  <TableCell className="font-medium">{ing.name}</TableCell>
                  <TableCell>{ing.category_name ?? '—'}</TableCell>
                  <TableCell>{ing.supplier_name ?? '—'}</TableCell>
                  <TableCell className="text-right font-mono">{formatCost(ing.gross_cost_per_kg)}</TableCell>
                  <TableCell className="text-right">{ing.sugars}%</TableCell>
                  <TableCell className="text-right">{ing.fat}%</TableCell>
                  <TableCell className="text-right">{ing.pac}</TableCell>
                  <TableCell>
                    <Badge variant={ing.is_active ? 'success' : 'secondary'}>
                      {ing.is_active ? t('common.active') : t('common.inactive')}
                    </Badge>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="w-20">
                      <div className="flex gap-1">
                        <Button asChild variant="ghost" size="icon">
                          <Link to={`/ingredients/${ing.id}/edit`}><Pencil size={14} /></Link>
                        </Button>
                        <Button
                          variant="ghost" size="icon"
                          className="text-destructive"
                          onClick={() => setDeleteId(ing.id)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3">
        {isLoading ? (
          <Spinner className="py-12" />
        ) : (data?.data ?? []).map(ing => (
          <Card key={ing.id} className="p-4">
            <div className="flex items-start justify-between">
              <div>
                <p className="font-semibold">{ing.name}</p>
                <p className="text-xs text-muted-foreground">{ing.category_name} · {ing.supplier_name}</p>
              </div>
              <Badge variant={ing.is_active ? 'success' : 'secondary'}>
                {ing.is_active ? t('common.active') : t('common.inactive')}
              </Badge>
            </div>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <div><span className="text-muted-foreground">Cost/kg </span>{formatCost(ing.gross_cost_per_kg)}</div>
              <div><span className="text-muted-foreground">Sugars </span>{ing.sugars}%</div>
              <div><span className="text-muted-foreground">PAC </span>{ing.pac}</div>
            </div>
            {isAdmin && (
              <div className="mt-3 flex gap-2">
                <Button asChild variant="outline" size="sm" className="flex-1">
                  <Link to={`/ingredients/${ing.id}/edit`}><Pencil size={13} /> {t('common.edit')}</Link>
                </Button>
                <Button variant="outline" size="sm" className="text-destructive" onClick={() => setDeleteId(ing.id)}>
                  <Trash2 size={13} />
                </Button>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {data && data.meta.totalPages > 1 && (
        <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
          <span>Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, data.meta.total)} of {data.meta.total}</span>
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button>
            <Button variant="outline" size="sm" disabled={page >= data.meta.totalPages} onClick={() => setPage(p => p + 1)}>Next</Button>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!deleteId}
        message={t('ingredients.deleteConfirm')}
        onConfirm={() => deleteId && deleteMutation.mutate(deleteId)}
        onCancel={() => setDeleteId(null)}
      />
    </div>
  );
}
